import React, { useEffect, useState } from 'react';
import { Typography, Box, Stack, Alert, Fade } from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';

const BRAND_RED = '#c53030';
const BRAND_PRIMARY = '#4040DB';

/**
 * Strona logowania.
 * Logowanie odbywa się wyłącznie przez Google (Internal OAuth dla domeny @unitresco.com),
 * więc nie ma żadnego formularza e-mail / hasła ani innych dostawców.
 */
const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const logoutReason = searchParams.get('reason');
  const errorCode = searchParams.get('error');

  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (logoutReason === 'idle') {
      setInfoMessage('Zostałeś automatycznie wylogowany z powodu bezczynności (15 minut bez aktywności).');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [logoutReason, searchParams, setSearchParams]);

  useEffect(() => {
    if (!errorCode) return;
    const map: Record<string, string> = {
      db: 'Błąd bazy danych po stronie serwera. Spróbuj ponownie za chwilę.',
      user_db: 'Nie udało się utworzyć lub zaktualizować konta użytkownika.',
      oauth: 'Nie udało się zweryfikować konta Google. Spróbuj zalogować się ponownie.',
      no_user: 'Konto Google nie zostało rozpoznane. Upewnij się, że używasz adresu @unitresco.com.',
      token: 'Nie udało się wygenerować tokenu sesji. Spróbuj jeszcze raz.',
      server: 'Wystąpił nieoczekiwany błąd serwera. Spróbuj ponownie.',
    };
    setErrorMessage(map[errorCode] || 'Logowanie nie powiodło się. Spróbuj ponownie.');
    searchParams.delete('error');
    setSearchParams(searchParams, { replace: true });
  }, [errorCode, searchParams, setSearchParams]);

  if (isAuthenticated && !isLoading) {
    const redirectTo = searchParams.get('redirect') || '/';
    return <Navigate to={redirectTo} replace />;
  }

  const year = new Date().getFullYear();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // Subtelny gradient w brandowych barwach (czerwień + primary aplikacji) z białym środkiem
        background: `
          radial-gradient(1200px 600px at 10% -10%, rgba(197, 48, 48, 0.12), transparent 60%),
          radial-gradient(900px 500px at 100% 110%, rgba(64, 64, 219, 0.10), transparent 60%),
          linear-gradient(180deg, #fafbfc 0%, #f2f3f7 100%)
        `,
        px: 2,
        py: { xs: 4, sm: 6 },
      }}
    >
      {/* Dekoracyjna siatka w tle */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(17,24,39,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(17,24,39,0.04) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.9), transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.9), transparent 75%)',
          pointerEvents: 'none',
        }}
      />

      <Fade in timeout={450}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 460,
            borderRadius: 4,
            px: { xs: 3.5, sm: 5 },
            py: { xs: 4, sm: 5 },
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(17,24,39,0.06)',
            boxShadow: '0 30px 60px -20px rgba(17,24,39,0.25), 0 12px 24px -12px rgba(17,24,39,0.12)',
          }}
        >
          {/* Czerwony cienki akcent na górze karty */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 24,
              right: 24,
              height: 3,
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
              background: `linear-gradient(90deg, ${BRAND_RED} 0%, ${BRAND_PRIMARY} 100%)`,
            }}
          />

          {/* Logo */}
          <Box sx={{ mb: 3.5 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '22px',
                lineHeight: '26px',
                fontWeight: 700,
                color: BRAND_RED,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                m: 0,
              }}
            >
              UNITRESCO
            </Typography>
          </Box>

          {/* Nagłówek */}
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: '1.6rem', sm: '1.9rem' },
              lineHeight: 1.15,
              fontWeight: 700,
              color: '#111827',
              mb: 3.5,
              letterSpacing: '-0.01em',
            }}
          >
            Witaj z powrotem.
          </Typography>

          {/* Komunikaty */}
          <Stack spacing={1.5} sx={{ mb: infoMessage || errorMessage ? 3 : 0 }}>
            {infoMessage && (
              <Alert severity="info" onClose={() => setInfoMessage(null)} variant="outlined" sx={{ borderRadius: 2 }}>
                {infoMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage(null)} variant="outlined" sx={{ borderRadius: 2 }}>
                {errorMessage}
              </Alert>
            )}
          </Stack>

          {/* Przycisk Google */}
          <GoogleLoginButton fullWidth size="large" />

          {/* Info o bezpiecznym logowaniu */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 2.5, color: '#6b7280', fontSize: '0.8rem' }}
          >
            <LockRoundedIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontSize: '0.78rem' }}>
              Dostęp wyłącznie dla pracowników Unitresco.
            </Typography>
          </Stack>

        </Box>
      </Fade>

      {/* Stopka całej strony */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: '0.75rem',
          pointerEvents: 'none',
        }}
      >
        © {year} Unitresco sp. z o. o.
      </Box>
    </Box>
  );
};

export default LoginPage;
